import { Controller, useForm } from "react-hook-form";

import { Button, Input, Select, toastError } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  type OwnerDetails,
  patchEmploymentStatus,
} from "~/v2/api/rest/account";
import { Field } from "~/v2/components/form";
import { useLock, useMutationRequest } from "../../../../api/hooks";
import { Back } from "../back";
import type { StepProps } from "../session";

export const EMPLOYMENT_OPTIONS = [
  { value: "EMPLOYED", label: "Employed" },
  { value: "UNEMPLOYED", label: "Unemployed" },
  { value: "RETIRED", label: "Retired" },
  { value: "STUDENT", label: "Student" },
];

type Fields = {
  employment_status: string;
  employer: string;
  employer_address: string;
  function: string;
  position: string;
};

const Employment = ({ session }: StepProps) => {
  const lock = useLock();

  // we need session below, so we spread these here
  const { account, details, next } = session;

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<Fields>({
    mode: "all",
    defaultValues: {
      employer: details?.employer || "",
      employer_address: details?.employer_address || "",
      function: details?.function || "",
      position: details?.position || "",
      employment_status: details?.employment_status || "",
    },
  });

  const status = watch("employment_status");

  const mutation = useMutationRequest((payload: Partial<OwnerDetails>) =>
    patchEmploymentStatus(account.id, payload),
  );

  const sendPatch = (values: Fields) => {
    if (lock.locked) {
      return;
    }

    lock.lock();

    mutation.mutate(
      (
        [
          "employer",
          "employer_address",
          "function",
          "position",
          "employment_status",
        ] as Array<keyof Fields>
      ).reduce<Partial<OwnerDetails>>((acc, key) => {
        if (values[key]) {
          acc[key] = values[key];
        }
        return acc;
      }, {}),
      {
        onSuccess: () => {
          // call amplitude event
          AmplitudeProvider.dispatch("onboardingv2_employment_step_complete");

          // go to next step
          next();
        },
        onError: () => {
          toastError("Error updating employment status");
        },
        onSettled: () => lock.unlock(),
      },
    );
  };

  return (
    <div className="mb-16">
      <h2 className="mb-8 font-bold text-lg">{session.step.title}</h2>
      <form onSubmit={handleSubmit(sendPatch)}>
        <div>
          <Field
            label="Employment Status"
            required={true}
            error={!!errors.employment_status}
            errorMsg="Required"
          >
            <Controller
              name="employment_status"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  placeholder="None selected"
                  options={EMPLOYMENT_OPTIONS}
                  {...field}
                />
              )}
            />
          </Field>
        </div>
        {status === "EMPLOYED" && (
          <>
            <div className="mt-4 flex">
              <div className="mr-4 w-[50%]">
                <Field
                  label="Employer"
                  required={true}
                  error={!!errors.employer}
                  errorMsg="Required"
                >
                  <Controller
                    name="employer"
                    control={control}
                    rules={{ required: status === "EMPLOYED" }}
                    render={({ field }) => <Input {...field} />}
                  />
                </Field>
              </div>
              <div className="mr-4 w-[50%]">
                <Field
                  label="Position"
                  required={true}
                  error={!!errors.position}
                  errorMsg="Required"
                >
                  <Controller
                    name="position"
                    control={control}
                    rules={{ required: status === "EMPLOYED" }}
                    render={({ field }) => <Input {...field} />}
                  />
                </Field>
              </div>
              <div className="w-[50%]">
                <Field
                  label="Function"
                  required={true}
                  error={!!errors.function}
                  errorMsg="Required"
                >
                  <Controller
                    name="function"
                    control={control}
                    rules={{ required: status === "EMPLOYED" }}
                    render={({ field }) => <Input {...field} />}
                  />
                </Field>
              </div>
            </div>
            <div className="mt-4">
              <Field
                label="Employer Address"
                required={true}
                error={!!errors.employer_address}
                errorMsg="Required"
              >
                <Controller
                  name="employer_address"
                  control={control}
                  rules={{ required: status === "EMPLOYED" }}
                  render={({ field }) => <Input {...field} />}
                />
              </Field>
            </div>
          </>
        )}
        <div className="mt-8 flex justify-end">
          <Back patch={true} session={session} />
          <Button
            className="ml-1"
            type="submit"
            disabled={isSubmitting || !isValid}
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Employment;
